import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useTheme } from '@react-navigation/native';
import { Icon as CustomIcon } from '@components';
import { Theme } from '@theme';
import { useSafeRoute } from '../../../navigation/web/useSafeNavigation';
import { Contact, useGetHashtagById, usePaginateHashtagContacts, useScreen } from '@hooks';
import { SafeAreaView } from 'react-native-safe-area-context';
import { defaultImage } from '@assets';
import { useLanguage } from '@context';

type Props = {
  params?: {
    hashtagId: string;
  },
  onEdit?: () => void;
};

export const HashtagDetails: React.FC<Props> = ({ params, onEdit }) => {
  const navigation = useNavigation<any>();
  const theme = useTheme();
  const {isMobile} = useScreen();
  const { t } = useLanguage();
  const styles = getStyles(theme);
  const route: any = useSafeRoute();
  
  const { hashtagId } = params || route?.params;
  const { data: hashtag, isFetched } = useGetHashtagById(hashtagId);
  const { flatData: contacts } = usePaginateHashtagContacts(hashtagId);

  if (!hashtag) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <CustomIcon name="arrowBack" size={25} color={theme.colors.black} />
          </TouchableOpacity>
        </View>
        <View style={styles.errorContainer}>
          {!isFetched && <ActivityIndicator size="large" color={theme.colors.primary} />}
          {isFetched && <Text style={styles.errorText}>{t('hashtagsScreen.hashtagNotFound')}</Text>}
        </View>
      </SafeAreaView>
    );
  }

  const handleEdit = () => {
    if(onEdit) {
      onEdit();
    } else {
      navigation.navigate('EditHashtag', { hashtagId: hashtag?.id });
    }
  };

  const renderContactItem = (contact: Contact) => (
    <TouchableOpacity key={contact.id} style={styles.contactItem} onPress={() => (navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: contact.id }
    })}>
      <Image source={!contact.avatarUrl? defaultImage : { uri: contact.avatarUrl }} style={styles.contactAvatar} />
      <Text style={styles.contactName}>{contact.fullName}</Text>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        {isMobile && <TouchableOpacity onPress={() => navigation.goBack()}>
          <CustomIcon name="arrowBack" size={25} color={theme.colors.black} />
        </TouchableOpacity>}
        <Text style={styles.hashtagTitle}>{hashtag?.name}</Text>
        <TouchableOpacity onPress={handleEdit}>
          <CustomIcon name="edit" size={20} color={theme.colors.primary} />
        </TouchableOpacity>
      </View>
      <Text style={styles.sectionTitle}>{t('common.personCount', { count: contacts.length })}</Text>
      <ScrollView style={styles.contactsList} showsVerticalScrollIndicator={!isMobile}>
        {contacts.map(renderContactItem)}
      </ScrollView>
    </SafeAreaView>
  );
};

const getStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: theme.spacing.md,
    backgroundColor: 'white',
    borderBottomWidth: 0.5,
    borderBottomColor: '#C7C7CC',
  },
  hashtagTitle: {
    fontSize: theme.fontSize.large,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  sectionTitle: {
    fontSize: 13,
    color: '#8E8E93',
    fontWeight: '400',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  contactsList: {
    flex: 1,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.grey20,
  },
  contactAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  contactName: {
    flex: 1,
    fontSize: 16,
    color: '#000',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    color: '#8E8E93',
  },
});

export default HashtagDetails; 